import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Cookie } from "lucide-react";
import PillButton from "./PillButton";

const STORAGE_KEY = "alva_cookie_consent_v1";

/**
 * Bandeau de consentement RGPD.
 * - S'affiche au premier chargement si aucun choix mémorisé.
 * - Stocke le choix dans localStorage : { necessary, analytics, marketing, ts }.
 * - Aucun cookie de tracking n'est déposé tant que l'utilisateur n'a pas accepté.
 * - Bouton flottant pour rouvrir les préférences (Cookies).
 */
export default function CookieBanner() {
    const [open, setOpen] = useState(false);
    const [showFab, setShowFab] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

    useEffect(() => {
        let stored = null;
        try {
            stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
        } catch (_) {
            stored = null;
        }
        if (!stored) {
            // Petit délai pour éviter de bloquer le LCP
            const t = setTimeout(() => setOpen(true), 600);
            return () => clearTimeout(t);
        }
        setShowFab(true);
        setPrefs({
            analytics: !!stored.analytics,
            marketing: !!stored.marketing,
        });
    }, []);

    // Exposer l'API d'ouverture (footer "Cookies")
    useEffect(() => {
        window.__alvaOpenCookies = () => {
            setShowSettings(true);
            setOpen(true);
        };
        return () => {
            try { delete window.__alvaOpenCookies; } catch (_) {}
        };
    }, []);

    const persist = (choice) => {
        const payload = {
            necessary: true,
            analytics: !!choice.analytics,
            marketing: !!choice.marketing,
            ts: new Date().toISOString(),
        };
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (_) {}
        // Hook pour déclencher (ou non) le chargement d'un éventuel script analytics
        try { window.dispatchEvent(new CustomEvent("alva:consent", { detail: payload })); } catch (_) {}
        setPrefs({ analytics: payload.analytics, marketing: payload.marketing });
        setOpen(false);
        setShowSettings(false);
        setShowFab(true);
    };

    const acceptAll = () => persist({ analytics: true, marketing: true });
    const refuseAll = () => persist({ analytics: false, marketing: false });
    const savePrefs = () => persist(prefs);

    return (
        <>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 24 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed inset-x-0 bottom-0 z-[80] p-3 md:p-5"
                        role="dialog"
                        aria-live="polite"
                        aria-label="Consentement aux cookies"
                        data-testid="cookie-banner"
                    >
                        <div className="max-w-5xl mx-auto rounded-2xl bg-white border border-[#1A2F6E]/10 shadow-[0_24px_60px_-12px_rgba(26,47,110,0.25)] p-5 md:p-6">
                            <div className="flex items-start gap-3 md:gap-4">
                                <div className="hidden sm:inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-[#2B7BE4]/10 text-[#2B7BE4]">
                                    <Cookie size={18} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h2 className="font-display text-base md:text-lg font-bold text-[#1A2F6E]">
                                        On respecte votre vie privée
                                    </h2>
                                    <p className="mt-1 text-sm text-[#1A1A2E]/70 leading-relaxed">
                                        Nous utilisons des cookies strictement nécessaires au bon fonctionnement du site, et — uniquement avec votre accord — des cookies de mesure d'audience pour comprendre comment notre site est utilisé. Aucun cookie publicitaire ou de revente. Vous pouvez modifier votre choix à tout moment.{" "}
                                        <Link
                                            to="/politique-confidentialite"
                                            className="underline decoration-[#2B7BE4]/40 underline-offset-2 hover:text-[#2B7BE4]"
                                        >
                                            En savoir plus
                                        </Link>
                                        .
                                    </p>

                                    {showSettings && (
                                        <div className="mt-4 space-y-2" data-testid="cookie-settings">
                                            <PrefRow
                                                title="Strictement nécessaires"
                                                desc="Indispensables au fonctionnement (sécurité, préférences). Toujours actifs."
                                                checked
                                                disabled
                                            />
                                            <PrefRow
                                                title="Mesure d'audience"
                                                desc="Statistiques anonymes de visite (pages vues, pays). Anonymisé."
                                                checked={prefs.analytics}
                                                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                                                testId="cookie-toggle-analytics"
                                            />
                                            <PrefRow
                                                title="Marketing"
                                                desc="Personnalisation publicitaire. Désactivé par défaut."
                                                checked={prefs.marketing}
                                                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                                                testId="cookie-toggle-marketing"
                                            />
                                        </div>
                                    )}

                                    <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center sm:flex-wrap">
                                        {showSettings ? (
                                            <>
                                                <PillButton
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={savePrefs}
                                                    data-testid="cookie-save"
                                                    className="min-h-[44px]"
                                                >
                                                    Enregistrer mes choix
                                                </PillButton>
                                                <PillButton
                                                    variant="outlineLight"
                                                    size="sm"
                                                    onClick={acceptAll}
                                                    data-testid="cookie-accept-all"
                                                    className="!border-[#1A2F6E]/15 !text-[#1A2F6E] hover:!bg-[#EEF2FB] min-h-[44px]"
                                                >
                                                    Tout accepter
                                                </PillButton>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowSettings(false)}
                                                    className="text-sm text-[#1A1A2E]/60 hover:text-[#1A2F6E] underline underline-offset-2 sm:ml-auto min-h-[44px]"
                                                >
                                                    Retour
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <PillButton
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={acceptAll}
                                                    data-testid="cookie-accept"
                                                    className="min-h-[44px]"
                                                >
                                                    Accepter
                                                </PillButton>
                                                <PillButton
                                                    variant="outlineLight"
                                                    size="sm"
                                                    onClick={refuseAll}
                                                    data-testid="cookie-refuse"
                                                    className="!border-[#1A2F6E]/15 !text-[#1A2F6E] hover:!bg-[#EEF2FB] min-h-[44px]"
                                                >
                                                    Refuser
                                                </PillButton>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowSettings(true)}
                                                    className="text-sm text-[#1A2F6E] hover:text-[#2B7BE4] underline underline-offset-2 sm:ml-auto min-h-[44px]"
                                                    data-testid="cookie-customize"
                                                >
                                                    Personnaliser
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    aria-label="Fermer (refuser)"
                                    onClick={refuseAll}
                                    className="hidden md:inline-flex shrink-0 size-8 items-center justify-center rounded-full bg-[#1A2F6E]/5 text-[#1A2F6E] hover:bg-[#1A2F6E]/10 transition-colors"
                                    data-testid="cookie-close"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {showFab && !open && (
                <button
                    type="button"
                    onClick={() => { setShowSettings(true); setOpen(true); }}
                    aria-label="Modifier mes préférences cookies"
                    className="fixed bottom-4 left-4 z-[70] inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur border border-[#1A2F6E]/10 px-3 py-2 text-xs font-semibold text-[#1A2F6E] shadow-[0_10px_30px_-10px_rgba(26,47,110,0.25)] hover:bg-[#EEF2FB] transition-colors min-h-[44px]"
                    data-testid="cookie-fab"
                >
                    <Cookie size={14} className="text-[#2B7BE4]" />
                    Cookies
                </button>
            )}
        </>
    );
}

function PrefRow({ title, desc, checked, disabled, onChange, testId }) {
    return (
        <div className="flex items-start justify-between gap-4 rounded-xl bg-[#F7F7F5] border border-[#1A2F6E]/8 px-3 py-2.5">
            <div className="min-w-0">
                <p className="text-sm font-semibold text-[#1A2F6E]">{title}</p>
                <p className="text-xs text-[#1A1A2E]/60 mt-0.5">{desc}</p>
            </div>
            <label className={`relative inline-flex shrink-0 items-center cursor-pointer ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
                <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={!!checked}
                    disabled={disabled}
                    onChange={(e) => onChange && onChange(e.target.checked)}
                    data-testid={testId}
                />
                <span className="w-10 h-6 bg-[#1A2F6E]/15 rounded-full peer-checked:bg-[#2B7BE4] transition-colors" />
                <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-4" />
            </label>
        </div>
    );
}
